import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import { ErrorCode } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Dialog, Select, Skeleton, type DialogTone } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { changeStage, useStageOptions } from '../api';
import type { DealRecord, Requirement, StageBody, StageOption, Tone } from '../types';
import { EffectList, FieldBlock, FlowChip, RequirementList } from './bits';
import { NextStepFields, useNextStepForm } from './NextStepFields';
import styles from './dialogs.module.css';

/**
 * Deal-Pipeline 17–20 + D-02: one dialog for every stage change. The server's `stage-options` carry
 * the chrome (title, tone, arrow, labels, footer), the requirements (✓/✕/!), the effects and the
 * fields the target demands; a hard lock disables the primary button. Errors: 409
 * `TRANSITION_NOT_ALLOWED` renders the server's requirement list, 400 puts the field texts under the
 * fields.
 */

export interface StageDialogSubject {
  id: string;
  contactName: string;
  /** „WE 2.02 + 2.03 · Lindenhof" — the subtitle after the name. */
  line: string;
}

export interface StageDialogProps {
  open: boolean;
  subject: StageDialogSubject;
  /** Preselected target (a drop or the record's gate); without it the dialog offers the stage select. */
  targetStageId?: string | null | undefined;
  onClose: () => void;
  onDone: (deal: DealRecord, nextStep: 'book_appointment' | null) => void;
}

function dialogTone(tone: Tone): DialogTone {
  if (tone === 'success' || tone === 'danger' || tone === 'warning' || tone === 'info') return tone;
  return 'info';
}

function flowMeta(option: StageOption): string {
  if (option.kind === 'backward') return 'Rückwärtsbewegung';
  return option.dialog.setsLabel;
}

function reasonLabel(option: StageOption): { label: string; note: string } {
  if (option.kind === 'backward') return { label: 'Grund', note: 'Pflicht bei Rückwärts' };
  return { label: 'Grund für das Weitergehen', note: 'Pflicht bei Warnung' };
}

interface FieldErrors {
  reason?: string | undefined;
  nextStep?: string | undefined;
  notarizedOn?: string | undefined;
  purchasePriceReceivedOn?: string | undefined;
}

export function StageDialog({ open, subject, targetStageId, onClose, onDone }: StageDialogProps) {
  const options = useStageOptions(open ? subject.id : null);
  const formId = useId();
  const [stageId, setStageId] = useState<string | null>(targetStageId ?? null);
  const [reason, setReason] = useState('');
  const [notarizedOn, setNotarizedOn] = useState('');
  const [purchasePriceReceivedOn, setPurchasePriceReceivedOn] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [serverRequirements, setServerRequirements] = useState<Requirement[] | null>(null);
  const [pending, setPending] = useState(false);
  const nextStep = useNextStepForm('task');

  useEffect(() => {
    if (!open) return;
    setStageId(targetStageId ?? null);
    setReason('');
    setNotarizedOn('');
    setPurchasePriceReceivedOn('');
    setFieldErrors({});
    setBanner(null);
    setServerRequirements(null);
    nextStep.reset({ kind: 'task' });
    // the form resets when the dialog opens for another subject or target
  }, [open, subject.id, targetStageId]);

  const selectable = useMemo(
    () => (options.data?.options ?? []).filter((option) => option.kind !== 'same'),
    [options.data],
  );
  const option = useMemo(() => {
    if (!options.data) return null;
    const wanted = stageId ?? selectable.find((entry) => entry.kind === 'forward')?.stageId ?? null;
    return options.data.options.find((entry) => entry.stageId === wanted) ?? null;
  }, [options.data, selectable, stageId]);

  /** „Weiter mit: <open step>" is offered for forward moves only and is then the default. */
  const openStep = option?.kind === 'forward' ? (options.data?.openStep ?? null) : null;
  const hasOpenStep = openStep !== null;
  useEffect(() => {
    if (hasOpenStep) nextStep.setKind('existing');
    else if (nextStep.kind === 'existing') nextStep.setKind('task');
    // follows the target and the availability of the open step, not every render of the form
  }, [open, option?.stageId, hasOpenStep]);

  const requirements = serverRequirements ?? option?.requirements ?? [];
  const needsReason = option?.fields.includes('reason') ?? false;
  const needsNextStep = option?.fields.includes('nextStep') ?? false;
  const needsNotarized = option?.fields.includes('notarizedOn') ?? false;
  const needsReceived = option?.fields.includes('purchasePriceReceivedOn') ?? false;
  const allowed = option?.allowed ?? false;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!option || !allowed || pending) return;
    const errors: FieldErrors = {};
    if (needsReason && reason.trim() === '') errors.reason = 'Bitte geben Sie einen Grund an.';
    if (needsNotarized && notarizedOn === '') errors.notarizedOn = 'Pflichtangabe.';
    if (needsReceived && purchasePriceReceivedOn === '') errors.purchasePriceReceivedOn = 'Pflichtangabe.';
    const step = needsNextStep ? nextStep.build() : null;
    if (needsNextStep && step === null) errors.nextStep = 'Bitte legen Sie den nächsten Schritt fest.';
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    const body: StageBody = { stageId: option.stageId };
    if (needsReason) body.reason = reason.trim();
    if (step) body.nextStep = step;
    if (needsNotarized) body.notarizedOn = notarizedOn;
    if (needsReceived) body.purchasePriceReceivedOn = purchasePriceReceivedOn;
    setPending(true);
    setBanner(null);
    try {
      const deal = await changeStage(subject.id, body);
      onDone(deal, step?.kind === 'book_appointment' ? 'book_appointment' : null);
    } catch (error) {
      if (error instanceof ApiError && error.code === ErrorCode.TRANSITION_NOT_ALLOWED) {
        const list = error.details?.['requirements'];
        if (Array.isArray(list)) setServerRequirements(list as Requirement[]);
        setBanner(error.message);
      } else if (error instanceof ApiError && error.status === 400) {
        const fields = error.fields;
        setFieldErrors({
          reason: fields['reason'],
          nextStep: fields['nextStep'],
          notarizedOn: fields['notarizedOn'],
          purchasePriceReceivedOn: fields['purchasePriceReceivedOn'],
        });
        if (Object.keys(fields).length === 0) setBanner(error.message);
      } else {
        setBanner(errorMessage(error));
      }
    } finally {
      setPending(false);
    }
  }

  const tone = option ? dialogTone(option.dialog.tone) : 'info';
  const title = option?.dialog.title ?? 'Stage wechseln';
  const footerNote = option?.dialog.footer ?? null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={560}
      tone={tone}
      title={title}
      subtitle={`${subject.contactName} · ${subject.line}`}
      className={styles.stageDialog}
      initialFocus="container"
      footerNote={footerNote}
      footer={
        <>
          <Button variant="secondary" className={styles.footBtn} onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="dark"
            className={styles.footBtn}
            disabled={!option || !allowed}
            loading={pending}
          >
            {option?.dialog.primaryLabel || 'Weiter'}
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.stack} onSubmit={submit} noValidate>
        {options.isPending ? (
          <>
            <Skeleton variant="block" height={46} />
            <Skeleton variant="block" height={56} />
            <Skeleton variant="block" height={56} />
          </>
        ) : null}
        {options.isError ? <Banner tone="danger">{errorMessage(options.error)}</Banner> : null}
        {options.data && !targetStageId ? (
          <Select
            label="Ziel-Stage"
            size="compact"
            value={option?.stageId ?? ''}
            options={selectable.map((entry) => ({
              value: entry.stageId,
              label: entry.kind === 'backward' ? `${entry.name} · zurück` : entry.name,
            }))}
            onChange={(event) => {
              setStageId(event.target.value);
              setServerRequirements(null);
              setBanner(null);
              setFieldErrors({});
            }}
          />
        ) : null}
        {options.data && option ? (
          <>
            <FlowChip
              from={options.data.current.name}
              to={option.name}
              arrow={option.dialog.arrow}
              tone={option.dialog.tone}
              meta={flowMeta(option)}
            />
            {banner ? <Banner tone="danger">{banner}</Banner> : null}
            {requirements.length > 0 ? (
              <RequirementList title="Voraussetzungen" items={requirements} />
            ) : null}
            {option.effects.length > 0 ? (
              <EffectList title="Was der Schritt auslöst" items={option.effects} />
            ) : null}
            {needsReason ? (
              <FieldBlock
                label={reasonLabel(option).label}
                note={reasonLabel(option).note}
                error={fieldErrors.reason}
                htmlFor={`${formId}-reason`}
              >
                <textarea
                  id={`${formId}-reason`}
                  className={clsx(styles.input, styles.textarea, fieldErrors.reason && styles.inputInvalid)}
                  rows={2}
                  value={reason}
                  aria-invalid={fieldErrors.reason ? true : undefined}
                  onChange={(event) => setReason(event.target.value)}
                />
              </FieldBlock>
            ) : null}
            {needsNotarized ? (
              <FieldBlock
                label="Beurkundungsdatum"
                note="Pflichtangabe"
                error={fieldErrors.notarizedOn}
                htmlFor={`${formId}-notarized`}
              >
                <input
                  id={`${formId}-notarized`}
                  type="date"
                  className={clsx(styles.input, fieldErrors.notarizedOn && styles.inputInvalid)}
                  value={notarizedOn}
                  aria-invalid={fieldErrors.notarizedOn ? true : undefined}
                  onChange={(event) => setNotarizedOn(event.target.value)}
                />
              </FieldBlock>
            ) : null}
            {needsReceived ? (
              <FieldBlock
                label="Zahlungseingang am"
                note="Pflichtangabe"
                error={fieldErrors.purchasePriceReceivedOn}
                htmlFor={`${formId}-received`}
              >
                <input
                  id={`${formId}-received`}
                  type="date"
                  className={clsx(styles.input, fieldErrors.purchasePriceReceivedOn && styles.inputInvalid)}
                  value={purchasePriceReceivedOn}
                  aria-invalid={fieldErrors.purchasePriceReceivedOn ? true : undefined}
                  onChange={(event) => setPurchasePriceReceivedOn(event.target.value)}
                />
              </FieldBlock>
            ) : null}
            {needsNextStep && allowed ? (
              <NextStepFields form={nextStep} error={fieldErrors.nextStep} openStep={openStep} />
            ) : null}
          </>
        ) : null}
      </form>
    </Dialog>
  );
}
